import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { userReducer, userInitialState, UserActionTypes } from "./user.reducer";
import {
  saveData,
  loadData,
  deleteData,
} from "../../services/IndexedDBService";
import { initializeUserSession } from "../../auth/auth";

// IndexedDB key for the user state
const USER_STATE_KEY = "userSlice";

// Contexts for the user state and dispatch
const UserStateContext = createContext(null);
const UserDispatchContext = createContext(null);

// User Provider
export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, userInitialState);
  const readyRef = useRef(false);

  // Rehydrate from IndexedDB, then validate server session.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cached = await loadData(USER_STATE_KEY).catch(() => null);
        if (!cancelled && cached?.data?.currentUser) {
          dispatch({
            type: UserActionTypes.SESSION_FETCH_SUCCESS,
            payload: cached.data.currentUser,
          });
        }

        dispatch({ type: UserActionTypes.SESSION_FETCH_START });
        const user = await initializeUserSession();
        if (!cancelled) {
          dispatch({
            type: UserActionTypes.SESSION_FETCH_SUCCESS,
            payload: user,
          });
        }
      } catch (err) {
        if (!cancelled) {
          const payload =
            err?.message === "No user session found" ? null : err?.message;
          dispatch({
            type: UserActionTypes.SESSION_FETCH_FAILURE,
            payload,
          });
          if (payload === null) {
            await deleteData(USER_STATE_KEY).catch(() => {});
          }
        }
      } finally {
        readyRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Persist user slice (debounced) – keeps only minimal user info.
  useEffect(() => {
    if (!readyRef.current) return;
    const t = setTimeout(() => {
      if (state.currentUser && !state.loggingOut) {
        saveData(USER_STATE_KEY, { currentUser: state.currentUser });
      }
      // else: keep disk minimal; we can clear on logout via actions
    }, 500);
    return () => clearTimeout(t);
  }, [state.currentUser, state.loggingOut]);

  const stateValue = useMemo(() => state, [state]);

  return (
    <UserStateContext.Provider value={stateValue}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
};

export const useUserState = () => {
  const ctx = useContext(UserStateContext);
  if (!ctx) throw new Error("useUserState must be used within UserProvider");
  return ctx;
};

export const useUserDispatch = () => {
  const ctx = useContext(UserDispatchContext);
  if (!ctx) throw new Error("useUserDispatch must be used within UserProvider");
  return ctx;
};
